import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth, api } from '../store/auth';

const S = {
  card: {
    background: 'var(--surface)', border: '1px solid var(--border)',
    borderRadius: 8, padding: 20, marginBottom: 16,
  },
  btn: (bg) => ({
    padding: '9px 20px', borderRadius: 6, border: 'none', cursor: 'pointer',
    fontWeight: 600, fontSize: 13, background: bg || 'var(--accent)', color: '#fff',
    transition: 'opacity .15s',
  }),
  btnMuted: {
    padding: '9px 20px', borderRadius: 6, border: '1px solid var(--border)',
    background: 'transparent', color: 'var(--text)', cursor: 'pointer', fontSize: 13,
  },
  code: {
    fontFamily: 'monospace', fontSize: 12, color: '#f59e0b',
    background: '#f59e0b11', padding: '2px 6px', borderRadius: 4, wordBreak: 'break-all',
  },
};

const STATUS_COLOR = { building: '#f59e0b', ready: '#22c55e', failed: '#ef4444' };
const STATUS_LABEL = { building: 'Building…', ready: 'Ready', failed: 'Failed' };

const STAGE_STEPS = [
  { key: 'copying',   label: 'Copying source' },
  { key: 'patching',  label: 'Patching config' },
  { key: 'compiling', label: 'Running Gradle' },
  { key: 'uploading', label: 'Uploading to GitHub' },
];

function fmtDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

export default function ApkBuilder() {
  const { claims } = useAuth();
  const agentToken = claims?.agent_token;
  const isSuper    = !!claims?.super;

  const [jobId,    setJobId]    = useState(null);
  const [job,      setJob]      = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [showLog,  setShowLog]  = useState(false);

  const [allBuilds,        setAllBuilds]        = useState([]);
  const [allBuildsLoading, setAllBuildsLoading] = useState(false);

  const pollRef = useRef(null);

  // Poll active job
  useEffect(() => {
    if (!jobId || !job || job.status !== 'building') {
      clearInterval(pollRef.current);
      return;
    }
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/android/build/${jobId}`);
        setJob(r.data);
        if (r.data.status !== 'building') {
          clearInterval(pollRef.current);
          if (isSuper) fetchAllBuilds();
        }
      } catch {}
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [jobId, job?.status]); // eslint-disable-line

  const fetchAllBuilds = useCallback(async () => {
    setAllBuildsLoading(true);
    try {
      const r = await api.get('/android/builds');
      setAllBuilds(r.data.builds || []);
    } catch {}
    finally { setAllBuildsLoading(false); }
  }, []);

  useEffect(() => {
    if (isSuper) fetchAllBuilds();
  }, []); // eslint-disable-line

  const startBuild = async () => {
    setLoading(true);
    setJob(null);
    setJobId(null);
    setShowLog(false);
    try {
      const r = await api.post('/android/build');
      setJobId(r.data.job_id);
      setJob({ status: 'building', stage: 'queued', log: 'Build queued…', error: null });
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Build failed to start';
      setJob({ status: 'failed', stage: '', error: msg, log: '' });
    } finally {
      setLoading(false);
    }
  };

  const downloadApk = (jid) => {
    api.get(`/android/build/${jid}/download`, { responseType: 'blob' })
      .then(r => {
        const url = URL.createObjectURL(r.data);
        const dl = document.createElement('a');
        const adminHint = r.headers['content-disposition']?.match(/filename="(.+?)"/)?.[1]
          || `classrmm_agent_${jid}.apk`;
        dl.href = url;
        dl.download = adminHint;
        dl.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => alert('Download failed — APK may no longer be available on server'));
  };

  const deleteJob = async (jid) => {
    if (!window.confirm('Delete this build? The APK file will be removed.')) return;
    try {
      await api.delete(`/android/build/${jid}`);
      if (jid === jobId) { setJob(null); setJobId(null); }
      setAllBuilds(prev => prev.filter(b => b.job_id !== jid));
    } catch (err) {
      alert(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div style={{ maxWidth: 760 }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Android APK Builder</h2>
      <p style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 20 }}>
        {isSuper
          ? 'Super admin view — see all APKs built by every admin and download any of them.'
          : 'Builds a debug APK with your agent token baked in — ready to distribute to target devices.'}
      </p>

      {/* ── Regular admin: your token ── */}
      {!isSuper && (
        <div style={S.card}>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: .4 }}>
            Your Agent Token
          </div>
          {agentToken ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <code style={S.code}>{agentToken}</code>
              <button
                style={{ ...S.btnMuted, padding: '4px 12px', fontSize: 11 }}
                onClick={() => navigator.clipboard.writeText(agentToken)}
              >Copy</button>
            </div>
          ) : (
            <p style={{ color: '#f87171', fontSize: 13, margin: 0 }}>
              No token yet — log out and log back in to auto-generate one.
            </p>
          )}
          <p style={{ color: 'var(--muted)', fontSize: 12, marginTop: 10, marginBottom: 0 }}>
            Devices that install your APK will appear in your dashboard only, unless super admin reviews them.
          </p>
        </div>
      )}

      {/* ── Build card ── */}
      <div style={S.card}>
        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>
          {isSuper ? 'Build APK (as super admin)' : 'Build APK'}
        </h3>
        {isSuper && (
          <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12 }}>
            This builds an APK with the super admin's own token. To build for a specific admin,
            they should log in and use this page themselves.
          </p>
        )}

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            style={{ ...S.btn(), opacity: (loading || job?.status === 'building') ? 0.6 : 1 }}
            disabled={loading || job?.status === 'building' || (!isSuper && !agentToken)}
            onClick={startBuild}
          >
            {loading ? 'Starting…' : job?.status === 'building' ? 'Building…' : 'Build APK'}
          </button>

          {job?.status === 'ready' && (
            <button style={S.btn('#22c55e')} onClick={() => downloadApk(jobId)}>
              ↓ Download APK
            </button>
          )}

          {job?.status === 'ready' && (
            <ShareLinkButton githubUrl={job.github_url} jobId={jobId} />
          )}

          {job && <StatusBadge status={job.status} />}

          {job?.status === 'ready' && (
            <button
              style={{ ...S.btnMuted, padding: '6px 12px', fontSize: 12, color: '#ef4444', borderColor: '#ef444444' }}
              onClick={() => deleteJob(jobId)}
            >
              🗑 Delete
            </button>
          )}
        </div>

        {job?.status === 'building' && <BuildProgress stage={job.stage} />}
        {job?.status === 'failed' && job.error && <ErrorBox message={job.error} />}

        {job?.status === 'ready' && (
          <div style={{
            marginTop: 14, padding: 12, background: '#22c55e11',
            border: '1px solid #22c55e33', borderRadius: 6,
          }}>
            <div style={{ fontWeight: 600, color: '#4ade80', fontSize: 13, marginBottom: 4 }}>
              APK ready
            </div>
            <p style={{ margin: 0, fontSize: 12, color: 'var(--muted)' }}>
              <strong>↓ Download APK</strong> saves the file directly.{' '}
              {job.github_url
                ? <><strong>Share Install Link</strong> copies the direct GitHub download link.</>
                : <><strong>Share Install Link</strong> copies a link to the install instructions page.</>
              }
            </p>
          </div>
        )}

        {job?.log && job.log.length > 10 && (
          <BuildLogToggle log={job.log} show={showLog} setShow={setShowLog} />
        )}
      </div>

      {/* ── Super admin: all builds ── */}
      {isSuper && (
        <div style={S.card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>All APK Builds</h3>
            <button style={{ ...S.btnMuted, padding: '5px 12px', fontSize: 12 }} onClick={fetchAllBuilds}>
              Refresh
            </button>
          </div>

          {allBuildsLoading && <p style={{ color: 'var(--muted)', fontSize: 13 }}>Loading…</p>}
          {!allBuildsLoading && allBuilds.length === 0 && (
            <p style={{ color: 'var(--muted)', fontSize: 13 }}>
              No builds yet. Admins can build their APKs from this page after logging in.
            </p>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {allBuilds.map(b => (
              <div key={b.job_id} style={{
                background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6,
                padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
              }}>
                {/* Admin label */}
                <span style={{
                  padding: '2px 8px', borderRadius: 3, fontSize: 11, fontWeight: 700,
                  background: '#7c3aed22', border: '1px solid #7c3aed55', color: '#a78bfa',
                  flexShrink: 0,
                }}>
                  {b.admin_username || 'unknown'}
                </span>

                <code style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'monospace' }}>
                  {b.job_id}
                </code>

                {/* Timestamp */}
                {b.created_at && (
                  <span style={{ fontSize: 11, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                    🕐 {fmtDate(b.created_at)}
                  </span>
                )}

                <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                  token: <span style={{ color: '#f59e0b' }}>{b.token_hint}</span>
                </span>

                <StatusBadge status={b.status} small />

                {b.status === 'building' && b.stage && (
                  <span style={{ fontSize: 11, color: '#f59e0b' }}>
                    {STAGE_STEPS.find(s => s.key === b.stage)?.label || b.stage}…
                  </span>
                )}

                {b.status === 'failed' && b.error && (
                  <span style={{ fontSize: 11, color: '#f87171', flex: 1 }}>
                    {b.error.slice(0, 80)}{b.error.length > 80 ? '…' : ''}
                  </span>
                )}

                <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexShrink: 0 }}>
                  {b.status === 'building' && (
                    <button
                      style={{ ...S.btnMuted, padding: '4px 10px', fontSize: 11 }}
                      onClick={async () => {
                        const r = await api.get(`/android/build/${b.job_id}`);
                        setAllBuilds(prev => prev.map(x => x.job_id === b.job_id ? { ...x, ...r.data } : x));
                      }}
                    >
                      Refresh
                    </button>
                  )}
                  {b.status === 'ready' && (
                    <>
                      <button
                        style={{ ...S.btn('#22c55e'), padding: '4px 12px', fontSize: 11 }}
                        onClick={() => downloadApk(b.job_id)}
                      >
                        ↓ Download
                      </button>
                      <ShareLinkButton githubUrl={b.github_url} jobId={b.job_id} small />
                    </>
                  )}
                  {b.status !== 'building' && (
                    <button
                      style={{
                        padding: '4px 10px', borderRadius: 6, border: '1px solid #ef444433',
                        background: 'transparent', color: '#f87171', cursor: 'pointer', fontSize: 11,
                      }}
                      onClick={() => deleteJob(b.job_id)}
                    >
                      🗑
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── How to use ── */}
      <div style={S.card}>
        <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, color: 'var(--muted)' }}>How to use</h3>
        <ol style={{ paddingLeft: 20, fontSize: 12, color: 'var(--muted)', lineHeight: 2, margin: 0 }}>
          <li>Click <strong>Build APK</strong> — the server compiles the agent with your token baked in</li>
          <li>Wait for the build to finish (~30 sec), then click <strong>↓ Download APK</strong></li>
          <li>Send the APK file to the target device via any transfer method</li>
          <li>On the target device: open <strong>Settings → Install unknown apps</strong> and enable it for your file manager or browser</li>
          <li>Open the APK file and tap <strong>Install</strong></li>
          <li>The device will appear in your dashboard as soon as the agent connects</li>
        </ol>
      </div>
    </div>
  );
}

// ── Shared sub-components ──────────────────────────────────────────────────────

function StatusBadge({ status, small }) {
  const color = STATUS_COLOR[status] || '#888';
  const label = STATUS_LABEL[status] || status;
  return (
    <span style={{
      padding: small ? '2px 8px' : '4px 10px',
      borderRadius: 12, fontSize: small ? 11 : 12, fontWeight: 600,
      background: color + '22', border: `1px solid ${color}55`, color,
    }}>
      {label}
    </span>
  );
}

function BuildProgress({ stage }) {
  const stepIdx = STAGE_STEPS.findIndex(s => s.key === stage);
  const activeStep = stepIdx >= 0 ? stepIdx : 0;

  return (
    <div style={{ marginTop: 14 }}>
      {/* Step indicators */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 12, overflowX: 'auto' }}>
        {STAGE_STEPS.map((step, i) => {
          const done    = i < activeStep;
          const current = i === activeStep;
          const color   = done ? '#22c55e' : current ? '#f59e0b' : 'var(--muted)';
          return (
            <React.Fragment key={step.key}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 80 }}>
                <div style={{
                  width: 24, height: 24, borderRadius: '50%', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700,
                  background: done ? '#22c55e22' : current ? '#f59e0b22' : 'var(--surface)',
                  border: `2px solid ${color}`,
                  color,
                }}>
                  {done ? '✓' : i + 1}
                </div>
                <span style={{ fontSize: 10, color, textAlign: 'center', whiteSpace: 'nowrap' }}>
                  {step.label}
                  {current && <Spinner style={{ marginLeft: 4 }} />}
                </span>
              </div>
              {i < STAGE_STEPS.length - 1 && (
                <div style={{
                  flex: 1, height: 2, minWidth: 16,
                  background: i < activeStep ? '#22c55e' : 'var(--border)',
                  marginBottom: 18,
                }} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Animated progress bar */}
      <div style={{ height: 4, background: 'var(--border)', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{
          height: '100%', width: '100%', borderRadius: 2,
          background: 'linear-gradient(90deg, var(--accent) 0%, #7c3aed 50%, var(--accent) 100%)',
          backgroundSize: '200% 100%',
          animation: 'slide 1.8s linear infinite',
        }} />
      </div>
      <style>{`@keyframes slide { 0%{background-position:100% 0} 100%{background-position:-100% 0} }`}</style>
    </div>
  );
}

function ErrorBox({ message }) {
  return (
    <div style={{
      marginTop: 14, padding: 12, background: '#ef444411',
      border: '1px solid #ef444433', borderRadius: 6,
    }}>
      <div style={{ fontWeight: 600, color: '#f87171', fontSize: 13, marginBottom: 4 }}>Build failed</div>
      <pre style={{ margin: 0, fontSize: 12, color: '#fca5a5', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
        {message}
      </pre>
    </div>
  );
}

function BuildLogToggle({ log, show, setShow }) {
  return (
    <div style={{ marginTop: 14 }}>
      <button
        style={{ ...S.btnMuted, padding: '4px 12px', fontSize: 11 }}
        onClick={() => setShow(v => !v)}
      >
        {show ? 'Hide' : 'Show'} Build Log
      </button>
      {show && (
        <pre style={{
          marginTop: 8, padding: 12, background: '#0a0a0a', color: '#9ca3af',
          fontSize: 11, fontFamily: 'monospace', borderRadius: 6, maxHeight: 400,
          overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
          border: '1px solid var(--border)',
        }}>
          {log}
        </pre>
      )}
    </div>
  );
}

function Spinner() {
  return (
    <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
      border: '2px solid transparent', borderTopColor: '#f59e0b',
      animation: 'spin .7s linear infinite', verticalAlign: 'middle', marginLeft: 4,
    }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </span>
  );
}

function ShareLinkButton({ githubUrl, jobId, small }) {
  const [copied, setCopied] = useState(false);

  // Prefer GitHub direct download; fall back to VPS install page
  const shareUrl = githubUrl || `${window.location.origin}/android/install/${jobId}`;
  const isGitHub = !!githubUrl;

  const copy = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <button
      style={{
        padding: small ? '4px 12px' : '9px 18px',
        borderRadius: 6, border: '1px solid #6366f166',
        background: '#6366f111', color: '#818cf8',
        cursor: 'pointer', fontSize: small ? 11 : 13, fontWeight: 600,
        transition: 'all .15s',
      }}
      onClick={copy}
      title={shareUrl}
    >
      {copied
        ? '✓ Copied!'
        : isGitHub
          ? '🔗 Share GitHub Link'
          : '🔗 Share Install Link'}
    </button>
  );
}
